import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeftIcon, SparklesIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { DefaultService, GamesService, GameInput, TagSuggestions, MetadataFieldDefinition, BGGSearchResult, OnlineRulesResult } from '../generated-api';
import GameImageUpload from '../components/common/GameImageUpload';
import TagInput from '../components/common/TagInput';
import OwnerInput from '../components/common/OwnerInput';
import MetadataFieldsEditor from '../components/MetadataFieldsEditor';
import { useGames } from '../contexts/DataContext';
import { trackAction } from '../services/telemetry';

const NewGameLibraryPage: React.FC = () => {
  const navigate = useNavigate();
  const { invalidate: invalidateGames } = useGames();

  // Two-phase UI: search phase vs form phase
  const [showForm, setShowForm] = useState(false);

  // BGG inline search state
  const [bggQuery, setBggQuery] = useState('');
  const [bggResults, setBggResults] = useState<BGGSearchResult[]>([]);
  const [bggSearching, setBggSearching] = useState(false);
  const [bggLoadingDetails, setBggLoadingDetails] = useState<number | null>(null);
  const [bggError, setBggError] = useState<string | null>(null);
  const bggRequestIdRef = useRef(0);

  // Game fields
  const [name, setName] = useState<string>('');
  const [availableExtensions, setAvailableExtensions] = useState<string[]>([]);
  const [minTeams, setMinTeams] = useState<number>(2);
  const [maxTeams, setMaxTeams] = useState<number>(4);
  const [description, setDescription] = useState<string>('');
  const [extensionInput, setExtensionInput] = useState<string>('');
  const [owners, setOwners] = useState<string[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [availableOwners, setAvailableOwners] = useState<string[]>([]);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [playerMetadataFields, setPlayerMetadataFields] = useState<MetadataFieldDefinition[]>([]);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [bggImageUrl, setBggImageUrl] = useState<string | null>(null);
  const [bggId, setBggId] = useState<number | null>(null);

  // Online rules import state
  const [onlineRulesResults, setOnlineRulesResults] = useState<OnlineRulesResult[]>([]);
  const [selectedOnlineRules, setSelectedOnlineRules] = useState<OnlineRulesResult | null>(null);
  const [searchingOnlineRules, setSearchingOnlineRules] = useState(false);
  const [onlineRulesSearched, setOnlineRulesSearched] = useState(false);

  // Debounced BGG search
  useEffect(() => {
    if (!bggQuery.trim() || bggQuery.length < 2) {
      setBggResults([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      const currentRequestId = ++bggRequestIdRef.current;
      setBggSearching(true);
      setBggError(null);

      try {
        const results = await GamesService.searchBggGamesBggSearchGet({
          query: bggQuery.trim(),
          limit: 15,
        });
        if (currentRequestId === bggRequestIdRef.current) {
          setBggResults(results);
        }
      } catch (err) {
        if (currentRequestId === bggRequestIdRef.current) {
          console.error('BGG search failed:', err);
          setBggError('Failed to search BoardGameGeek. Please try again.');
          setBggResults([]);
        }
      } finally {
        if (currentRequestId === bggRequestIdRef.current) {
          setBggSearching(false);
        }
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [bggQuery]);

  // Fetch available tags when form is shown
  useEffect(() => {
    if (!showForm) return;
    const fetchTags = async () => {
      try {
        const tags = await DefaultService.getTagsTagsGet();
        setAvailableTags(tags);
      } catch (err) {
        console.error('Failed to fetch tags:', err);
      }
    };
    fetchTags();
  }, [showForm]);

  // Fetch available owners when form is shown
  useEffect(() => {
    if (!showForm) return;
    const fetchOwners = async () => {
      try {
        const owners = await DefaultService.getOwnersOwnersGet();
        setAvailableOwners(owners);
      } catch (err) {
        console.error('Failed to fetch owners:', err);
      }
    };
    fetchOwners();
  }, [showForm]);

  // Extension management
  const addExtension = () => {
    if (extensionInput.trim() && !availableExtensions.includes(extensionInput.trim())) {
      setAvailableExtensions([...availableExtensions, extensionInput.trim()]);
      setExtensionInput('');
    }
  };

  const removeExtension = (extensionToRemove: string) => {
    setAvailableExtensions(availableExtensions.filter(ext => ext !== extensionToRemove));
  };

  const getTagSuggestions = async () => {
    if (!name.trim()) {
      setError('Please enter a game name first to get tag suggestions.');
      return;
    }

    setLoadingSuggestions(true);
    try {
      const suggestions: TagSuggestions = await DefaultService.getTagSuggestionsByNameGamesTagSuggestionsByNamePost({
        requestBody: { name: name.trim() }
      });

      const newExistingTags = suggestions.existing.filter(tag => !availableTags.includes(tag));
      const newSuggestedTags = suggestions.new.filter(tag => !availableTags.includes(tag));

      if (newExistingTags.length > 0 || newSuggestedTags.length > 0) {
        setAvailableTags(prev => [...prev, ...newExistingTags, ...newSuggestedTags]);
      }

      const allSuggestions = [...suggestions.existing, ...suggestions.new];
      const tagsToAdd = allSuggestions.filter(tag => !tags.includes(tag));

      if (tagsToAdd.length > 0) {
        setTags(prev => [...prev, ...tagsToAdd]);
      }
    } catch (err) {
      console.error('Failed to get tag suggestions:', err);
      setError('Failed to get tag suggestions. Please try again.');
    } finally {
      setLoadingSuggestions(false);
    }
  };

  // Handler for when a BGG game is selected from inline search
  const handleBggSelect = useCallback(async (result: BGGSearchResult) => {
    setBggLoadingDetails(result.bggId);
    setBggError(null);

    try {
      const details = await GamesService.getBggGameDetailsGamesBggBggIdGet({
        bggId: result.bggId,
      });

      // Populate form fields from BGG details
      if (details.bggId) setBggId(details.bggId);
      setName(details.name);
      if (details.minPlayers) setMinTeams(details.minPlayers);
      if (details.maxPlayers) setMaxTeams(details.maxPlayers);
      if (details.description) setDescription(details.description);

      if (details.imageUrl) {
        setBggImageUrl(details.imageUrl);
        setImageFile(null);
      }

      let combinedTags: string[] = [];
      if (details.mappedTags && details.mappedTags.length > 0) {
        combinedTags = details.mappedTags.slice(0, 10);
      } else {
        if (details.categories) {
          for (const category of details.categories) {
            if (combinedTags.length < 10) combinedTags.push(category);
          }
        }
        if (details.mechanics) {
          for (const mechanic of details.mechanics) {
            if (combinedTags.length < 10 && !combinedTags.includes(mechanic)) combinedTags.push(mechanic);
          }
        }
      }

      if (combinedTags.length > 0) {
        setTags(combinedTags);
        setAvailableTags(prev => {
          const newTags = combinedTags.filter(t => !prev.includes(t));
          return [...prev, ...newTags];
        });
      }

      if (details.expansions && details.expansions.length > 0) {
        setAvailableExtensions(details.expansions);
      }

      // Show the form
      setShowForm(true);
    } catch (err) {
      console.error('Failed to get BGG game details:', err);
      setBggError('Failed to get game details. Please try again.');
    } finally {
      setBggLoadingDetails(null);
    }
  }, []);

  const searchOnlineRules = async () => {
    if (!name.trim()) return;
    setSearchingOnlineRules(true);
    setOnlineRulesSearched(true);
    try {
      const results = await DefaultService.searchOnlineRulesNoGameRulesSearchOnlineGet({
        query: name.trim(),
      });
      setOnlineRulesResults(results);
      if (results.length === 1) {
        setSelectedOnlineRules(results[0]);
      }
    } catch (err) {
      console.error('Failed to search online rules:', err);
      setOnlineRulesResults([]);
    } finally {
      setSearchingOnlineRules(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Game name is required.');
      return;
    }

    if (minTeams < 1) {
      setError('Minimum teams must be at least 1.');
      return;
    }

    if (maxTeams < minTeams) {
      setError('Maximum teams must be greater than or equal to minimum teams.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const validMetadataFields = playerMetadataFields.filter(field =>
        field.name.trim() &&
        field.displayName.trim() &&
        /^[a-zA-Z0-9_]+$/.test(field.name)
      );

      const gameInput: GameInput = {
        name: name.trim(),
        availableExtensions: availableExtensions,
        minTeams: minTeams,
        maxTeams: maxTeams,
        description: description.trim() || undefined,
        owners: owners.length > 0 ? owners : undefined,
        tags: tags.length > 0 ? tags : undefined,
        bggId: bggId || undefined,
        playerMetadataFields: validMetadataFields.length > 0 ? validMetadataFields : undefined
      };

      const newGame = await DefaultService.createGameGamesPost({
        requestBody: gameInput
      });
      trackAction('game_added', { from_bgg: Boolean(bggImageUrl) });

      if (imageFile) {
        await DefaultService.uploadGameImageGamesGameIdImagePost({
          gameId: newGame.id,
          formData: { image: imageFile }
        });
      } else if (bggImageUrl) {
        try {
          await GamesService.importBggImageGamesGameIdImportBggImagePost({
            gameId: newGame.id,
            imageUrl: bggImageUrl
          });
        } catch (imgErr) {
          console.error('Failed to import BGG image:', imgErr);
        }
      }

      // Trigger online rules import if a source was selected (fire-and-forget)
      if (selectedOnlineRules) {
        DefaultService.importOnlineRulesGamesGameIdRulesImportOnlinePost({
          gameId: newGame.id,
          requestBody: {
            slug: selectedOnlineRules.slug,
            name: selectedOnlineRules.name,
          },
        }).catch((err) => console.error('Failed to import online rules:', err));
      }

      invalidateGames();
      navigate(`/games/${newGame.id}`);
    } catch (err) {
      setError('Failed to create game. Please try again.');
      console.error('Error creating game:', err);
    } finally {
      setSubmitting(false);
    }
  };

  // --- Search phase ---
  if (!showForm) {
    return (
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <Link
              to="/games"
              className="mr-4 p-2 text-disabled hover:text-body"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </Link>
            <div>
              <h1 className="text-fluid-h1 font-bold text-heading">Add Game to Library</h1>
              <p className="mt-2 text-body">
                Search BoardGameGeek to get started
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-2xl">
          {/* Search Input */}
          <div className="relative mb-4">
            <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-disabled" />
            <input
              type="text"
              value={bggQuery}
              onChange={(e) => setBggQuery(e.target.value)}
              placeholder="Search for a board game..."
              className="w-full pl-12 pr-4 py-3 border border-edge-input rounded-lg text-lg focus:ring-primary-500 focus:border-primary-500"
              autoFocus
            />
          </div>

          {bggSearching && (
            <div className="text-sm text-muted mb-4 flex items-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500"></div>
              Searching BoardGameGeek...
            </div>
          )}

          {bggError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-800">
              {bggError}
            </div>
          )}

          {/* Results */}
          {bggResults.length > 0 && (
            <div className="bg-surface shadow rounded-lg border border-edge overflow-hidden mb-6">
              {bggResults.map((result) => (
                <button
                  data-track="bgg_result"
                  key={result.bggId}
                  onClick={() => handleBggSelect(result)}
                  disabled={bggLoadingDetails !== null}
                  className="w-full text-left p-4 hover:bg-surface-hover border-b border-edge-subtle last:border-b-0 focus:outline-none focus:bg-primary-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-heading">{result.name}</div>
                      {result.yearPublished && (
                        <div className="text-sm text-muted">Published: {result.yearPublished}</div>
                      )}
                    </div>
                    {bggLoadingDetails === result.bggId ? (
                      <div className="flex items-center text-primary-600 text-sm">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600 mr-2"></div>
                        Loading...
                      </div>
                    ) : (
                      <span className="text-primary-600 text-sm font-medium">Select</span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}

          {bggResults.length === 0 && bggQuery.length >= 2 && !bggSearching && (
            <div className="text-center text-muted py-8">
              No games found. Try a different search term.
            </div>
          )}

          {/* Manual create link */}
          <div className="text-center pt-4 border-t border-edge">
            <button
              onClick={() => setShowForm(true)}
              className="text-sm text-muted hover:text-primary-600 transition-colors"
            >
              Or create a game manually without importing
            </button>
          </div>
        </div>
      </div>
    );
  }

  // --- Form phase ---
  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <button
            onClick={() => setShowForm(false)}
            className="mr-4 p-2 text-disabled hover:text-body"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-fluid-h1 font-bold text-heading">Add Game to Library</h1>
            <p className="mt-2 text-body">
              Review and customize game details
            </p>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
            <div className="ml-auto pl-3">
              <button
                onClick={() => setError(null)}
                className="text-red-400 hover:text-red-600"
              >
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="bg-surface shadow rounded-lg p-6 max-w-2xl">
          <h2 className="text-lg font-medium text-heading mb-6">Game Details</h2>

          <div className="space-y-6">
            {/* Game Image */}
            <div>
              <label className="block text-sm font-medium text-body mb-3 text-center">
                Game Cover Image (Optional)
              </label>
              {bggImageUrl && !imageFile ? (
                <div className="mb-4">
                  <div className="relative w-48 h-48 mx-auto">
                    <img
                      src={bggImageUrl}
                      alt="BGG import"
                      className="w-full h-full object-cover rounded-lg shadow-md"
                    />
                    <button
                      type="button"
                      onClick={() => setBggImageUrl(null)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      title="Remove image"
                    >
                      <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                  <p className="text-xs text-muted text-center mt-2">
                    Image from BoardGameGeek (will be saved to our servers)
                  </p>
                </div>
              ) : (
                <GameImageUpload
                  onImageChange={(file) => {
                    setImageFile(file);
                    if (file) setBggImageUrl(null);
                  }}
                  className="mb-4"
                />
              )}
            </div>

            {/* Game Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-body mb-2">
                Game Name *
              </label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full border border-edge-input rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="e.g., Catan, Splendor, Wingspan"
                required
              />
            </div>

            {/* Team Configuration */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="minTeams" className="block text-sm font-medium text-body mb-2">
                  Minimum Teams *
                </label>
                <input
                  type="number"
                  id="minTeams"
                  value={minTeams}
                  onChange={(e) => setMinTeams(parseInt(e.target.value) || 1)}
                  className="w-full border border-edge-input rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                  min="1"
                  required
                />
              </div>
              <div>
                <label htmlFor="maxTeams" className="block text-sm font-medium text-body mb-2">
                  Maximum Teams *
                </label>
                <input
                  type="number"
                  id="maxTeams"
                  value={maxTeams}
                  onChange={(e) => setMaxTeams(parseInt(e.target.value) || 1)}
                  className="w-full border border-edge-input rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                  min="1"
                  required
                />
              </div>
            </div>

            {/* Available Extensions */}
            <div>
              <label htmlFor="extensionInput" className="block text-sm font-medium text-body mb-2">
                Available Extensions / Expansions
              </label>
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  id="extensionInput"
                  value={extensionInput}
                  onChange={(e) => setExtensionInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addExtension())}
                  className="flex-1 border border-edge-input rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Add extension name"
                />
                <button
                  type="button"
                  onClick={addExtension}
                  className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:ring-2 focus:ring-primary-500"
                >
                  Add
                </button>
              </div>
              {availableExtensions.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {availableExtensions.map((extension, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-1 rounded-md text-sm font-medium bg-purple-100 text-purple-800"
                    >
                      {extension}
                      <button
                        type="button"
                        onClick={() => removeExtension(extension)}
                        className="ml-1 text-purple-600 hover:text-purple-900"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <p className="text-xs text-muted mt-1">
                List all available extensions or expansions for this game
              </p>
            </div>

            {/* Owners */}
            <div>
              <label className="block text-sm font-medium text-body mb-2">
                Owners (Optional)
              </label>
              <OwnerInput
                owners={owners}
                availableOwners={availableOwners}
                onOwnersChange={setOwners}
                placeholder="Add owner (e.g., John, Mary, Alex...)"
                maxOwners={10}
              />
              <p className="text-xs text-muted mt-1">
                List who owns this game (useful for shared game collections)
              </p>
            </div>

            {/* Tags */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-body">
                  Tags (Optional)
                </label>
                <button
                  type="button"
                  onClick={getTagSuggestions}
                  disabled={loadingSuggestions}
                  className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-purple-700 bg-purple-100 hover:bg-purple-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <SparklesIcon className="h-4 w-4 mr-1" />
                  {loadingSuggestions ? 'Getting suggestions...' : 'Get AI suggestions'}
                </button>
              </div>
              <TagInput
                tags={tags}
                availableTags={availableTags}
                onTagsChange={setTags}
                placeholder="Add tag (e.g., strategy, cards, tile-placement...)"
                maxTags={10}
              />
              <p className="text-xs text-muted mt-1">
                Add tags to categorize this game (maximum 10)
              </p>
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-body mb-2">
                Description (Optional)
              </label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="w-full border border-edge-input rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Brief description of the game..."
              />
            </div>

            {/* Online Rules Import */}
            <div>
              <label className="block text-sm font-medium text-body mb-2">
                Import Rules Online (Optional)
              </label>
              {!onlineRulesSearched ? (
                <button
                  type="button"
                  onClick={searchOnlineRules}
                  disabled={!name.trim() || searchingOnlineRules}
                  className="inline-flex items-center px-4 py-2 border border-edge-input rounded-md text-sm font-medium text-body bg-surface hover:bg-surface-hover disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {searchingOnlineRules ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                      Searching...
                    </>
                  ) : (
                    <>
                      <MagnifyingGlassIcon className="h-4 w-4 mr-2" />
                      Find rules online
                    </>
                  )}
                </button>
              ) : searchingOnlineRules ? (
                <div className="text-sm text-muted flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500"></div>
                  Searching for rules...
                </div>
              ) : onlineRulesResults.length > 0 ? (
                <div className="space-y-2">
                  {onlineRulesResults.map((result) => (
                    <label
                      key={result.slug}
                      className={`flex items-center p-3 border rounded-md cursor-pointer transition-colors ${
                        selectedOnlineRules?.slug === result.slug
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-edge hover:bg-surface-hover'
                      }`}
                    >
                      <input
                        type="radio"
                        name="onlineRules"
                        checked={selectedOnlineRules?.slug === result.slug}
                        onChange={() => setSelectedOnlineRules(result)}
                        className="h-4 w-4 text-primary-600 border-edge-input"
                      />
                      <span className="ml-3 text-sm text-heading">{result.name}</span>
                    </label>
                  ))}
                  {selectedOnlineRules && (
                    <button
                      type="button"
                      onClick={() => setSelectedOnlineRules(null)}
                      className="text-xs text-disabled hover:text-body"
                    >
                      Don't import rules
                    </button>
                  )}
                </div>
              ) : (
                <div className="text-sm text-muted">
                  No rules found online for this game.
                  <button
                    type="button"
                    onClick={() => { setOnlineRulesSearched(false); setOnlineRulesResults([]); }}
                    className="ml-2 text-primary-600 hover:text-primary-800"
                  >
                    Try again
                  </button>
                </div>
              )}
              <p className="text-xs text-muted mt-1">
                Automatically import rules and generate a summary when the game is created
              </p>
            </div>

            {/* Player Metadata Fields */}
            <div>
              <MetadataFieldsEditor
                fields={playerMetadataFields}
                onFieldsChange={setPlayerMetadataFields}
              />
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-edge flex space-x-3">
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Adding Game...' : 'Add to Library'}
            </button>
            <Link
              to="/games"
              className="flex-1 flex justify-center py-2 px-4 border border-edge-input rounded-md shadow-sm text-sm font-medium text-body bg-surface hover:bg-surface-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Cancel
            </Link>
          </div>
        </div>
      </form>
    </div>
  );
};

export default NewGameLibraryPage;
